// src/composables/useErrorHandling.ts
import { ref, computed, getCurrentInstance, type Ref } from 'vue'
import { useToast } from 'vue-toastification'
import { useQueryClient } from '@tanstack/vue-query'
import { clearAllCache } from '@/utils/cacheUtils'

export interface ErrorState {
  hasError: boolean
  message: string
  type: 'network' | 'auth' | 'validation' | 'server' | 'unknown'
  retryable: boolean
  context?: any
}

/**
 * Composable for centralized error handling across the application
 * Provides consistent error messaging, retry logic, and offline fallbacks
 */
export function useErrorHandling() {
  // Only use Vue composables if we're in a component context
  const toast = getCurrentInstance() ? useToast() : null
  const queryClient = getCurrentInstance() ? useQueryClient() : null
  
  const globalError = ref<ErrorState | null>(null)
  const isOffline = ref(!navigator.onLine)
  const authErrors = ref<string[]>([])

  // Watch for online/offline status changes
  if (typeof window !== 'undefined') {
    const updateOnlineStatus = () => {
      isOffline.value = !navigator.onLine
    }
    
    window.addEventListener('online', updateOnlineStatus)
    window.addEventListener('offline', updateOnlineStatus)
  }

  // Error classification
  const classifyError = (error: any): ErrorState['type'] => {
    if (!error) return 'unknown'
    
    const message = error.message?.toLowerCase() || ''
    const status = error.status || error.response?.status
    
    if (message.includes('authentication') || message.includes('unauthorized') || status === 401 || status === 403) {
      return 'auth'
    }
    
    if (message.includes('network') || message.includes('fetch') || !navigator.onLine || status >= 500) {
      return 'network'
    }
    
    if (status >= 400 && status < 500) {
      return 'validation'
    }
    
    if (status >= 500) {
      return 'server'
    }
    
    return 'unknown'
  }

  // Check if error is retryable
  const isRetryable = (errorType: ErrorState['type'], error: any): boolean => {
    switch (errorType) {
      case 'network':
        return true
      case 'server':
        return true
      case 'auth':
        return false // Don't auto-retry auth errors
      case 'validation':
        return false // Don't retry validation errors
      default:
        return error?.status >= 500
    }
  }

  // Get user-friendly error message
  const getUserFriendlyMessage = (error: any, type: ErrorState['type']): string => {
    switch (type) {
      case 'auth':
        return 'Your session has expired. Please log in again.'
      case 'network':
        if (!navigator.onLine) {
          return 'You\'re offline. Some features may not be available.'
        }
        return 'Network connection error. Please check your internet connection.'
      case 'server':
        return 'Server error. Our team has been notified. Please try again later.'
      case 'validation':
        return error?.message || 'Invalid data provided. Please check your input.'
      default:
        return error?.message || 'An unexpected error occurred. Please try again.'
    }
  }

  // Handle different types of errors
  const handleError = (error: any, options: {
    context?: string
    showToast?: boolean
    clearCache?: boolean
    redirect?: string
  } = {}) => {
    const { context, showToast = true, clearCache = false, redirect } = options
    
    console.error('Error in', context || 'unknown context', ':', error)
    
    const type = classifyError(error)
    const message = getUserFriendlyMessage(error, type)
    const retryable = isRetryable(type, error)
    
    const errorState: ErrorState = {
      hasError: true,
      message,
      type,
      retryable,
      context: context
    }
    
    globalError.value = errorState
    
    // Handle authentication errors
    if (type === 'auth') {
      authErrors.value.push(message)
      if (clearCache) {
        console.log('Auth error: clearing cache')
        clearAllCache()
        queryClient?.clear()
      }
      
      // Could trigger logout here
      // router.push('/login')
    }
    
    // Show toast notification
    if (showToast && toast) {
      switch (type) {
        case 'auth':
          toast.error(message, { timeout: 10000 })
          break
        case 'network':
          if (navigator.onLine) {
            toast.warning(message, { timeout: 5000 })
          } else {
            toast.info(message, { timeout: 3000 })
          }
          break
        case 'server':
          toast.error(message, { timeout: 8000 })
          break
        case 'validation':
          toast.warning(message, { timeout: 5000 })
          break
        default:
          toast.error(message)
      }
    }
    
    return errorState
  }

  // Handle query errors specifically
  const handleQueryError = (error: any, queryKey: string[]) => {
    const context = `Query: ${queryKey.join(':')}`
    return handleError(error, { 
      context,
      clearCache: classifyError(error) === 'auth'
    })
  }

  // Handle mutation errors specifically  
  const handleMutationError = (error: any, mutationContext: string) => {
    return handleError(error, { 
      context: `Mutation: ${mutationContext}`,
      showToast: true
    })
  }

  // Clear global error
  const clearError = () => {
    globalError.value = null
  }

  // Clear auth errors
  const clearAuthErrors = () => {
    authErrors.value = []
  }

  // Retry with exponential backoff
  const retryWithBackoff = async (
    fn: () => Promise<any>,
    maxRetries = 3,
    initialDelay = 1000
  ): Promise<any> => {
    let lastError: any
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await fn()
      } catch (error) {
        lastError = error
        
        if (attempt === maxRetries) {
          throw error
        }
        
        const errorType = classifyError(error)
        if (!isRetryable(errorType, error)) {
          throw error
        }
        
        const delay = initialDelay * Math.pow(2, attempt)
        console.log(`Retry attempt ${attempt + 1} after ${delay}ms`)
        
        await new Promise(resolve => setTimeout(resolve, delay))
      }
    }
    
    throw lastError
  }

  // Network status helpers
  const isOnline = computed(() => !isOffline.value)
  const hasNetworkError = computed(() => globalError.value?.type === 'network')
  const hasAuthError = computed(() => globalError.value?.type === 'auth' || authErrors.value.length > 0)
  
  // Offline fallback message
  const getOfflineFallbackMessage = (action: string) => {
    return `${action} is not available offline. Your changes will be saved when you're back online.`
  }

  // Check if action should be disabled offline
  const isDisabledOffline = (requiresNetwork: boolean = true) => {
    return requiresNetwork && isOffline.value
  }

  return {
    // State
    globalError: computed(() => globalError.value),
    isOffline: computed(() => isOffline.value),
    isOnline,
    hasNetworkError,
    hasAuthError,
    authErrors: computed(() => authErrors.value),
    
    // Error handling
    handleError,
    handleQueryError,
    handleMutationError,
    clearError,
    clearAuthErrors,
    
    // Retry logic
    retryWithBackoff,
    
    // Offline helpers
    getOfflineFallbackMessage,
    isDisabledOffline,
    
    // Utilities
    classifyError,
    getUserFriendlyMessage,
  }
}

// Global error handler for uncaught errors
export function setupGlobalErrorHandling() {
  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    console.error('Unhandled promise rejection:', event.reason)
    
    // Check if it's an auth error and clear cache
    const message = event.reason?.message?.toLowerCase() || ''
    if (message.includes('authentication') || message.includes('unauthorized')) {
      console.warn('Global auth error detected, clearing cache')
      clearAllCache()
    }
    
    // Prevent the default browser error reporting
    event.preventDefault()
  })
  
  // Handle uncaught errors
  window.addEventListener('error', (event) => {
    console.error('Uncaught error:', event.error)
    
    // Log errors for debugging in development
    if (import.meta.env.DEV) {
      console.group('🐛 Uncaught Error Details')
      console.error('Error:', event.error)
      console.error('Filename:', event.filename)
      console.error('Line:', event.lineno, 'Column:', event.colno)
      console.groupEnd()
    }
  })
}

// Utility function to wrap async functions with error handling
// Note: This should only be used inside Vue components where composables are available
export function withErrorHandling<T extends (...args: any[]) => Promise<any>>(
  fn: T,
  context: string = 'Unknown'
): T {
  return (async (...args: any[]) => {
    try {
      return await fn(...args)
    } catch (error) {
      console.error(`Error in ${context}:`, error)
      
      // Basic error handling without composables
      const message = error instanceof Error ? error.message : String(error)
      
      // Check if it's an auth error
      if (message.toLowerCase().includes('auth') || message.toLowerCase().includes('unauthorized')) {
        console.warn('Auth error detected, clearing cache')
        clearAllCache()
      }
      
      throw error // Re-throw so calling code can handle it
    }
  }) as T
}